import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, formatINR, istDate, istMonth } from "@/lib/api";
import { CalendarRange, Clock, Edit3, Lock, Plus, RefreshCw, SlidersHorizontal, Trash2, Users, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import MonthSelect from "@/components/MonthSelect";

const EMPTY_TIER = { basis: "amount", min_amount: 0, min_target_percent: 0, amount: 0, unit: "flat" };
const EMPTY_RULE = {
  type: "daily",
  name: "",
  date: istDate(),
  start_date: istDate(),
  end_date: istDate(),
  month: istMonth(),
  start_time: "09:00",
  end_time: "10:00",
  tiers: [{ ...EMPTY_TIER }],
  all_employees: true,
  employee_ids: [],
  auto_delete: true,
};

export default function IncentivesTab() {
  const [month, setMonth] = useState(istMonth());
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rules, setRules] = useState([]);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState({ ...EMPTY_RULE });
  const [ruleDelete, setRuleDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatingAll, setUpdatingAll] = useState(false);

  const load = () => Promise.all([
    api.get(`/admin/incentives?month=${month}`).then((r) => setRows(r.data.results || [])),
    api.get("/admin/incentive-rules").then((r) => setRules(r.data || [])),
    api.get("/admin/employees").then((r) => setEmployees((r.data || []).filter((e) => e.role === "employee" || e.basic_salary !== undefined))),
  ]);

  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(load, [month], { enabled: !ruleOpen && !editing && !saving && !updatingAll, intervalMs: 30000 });

  const activeRules = rules.filter((r) => ["active", "upcoming"].includes(r.state));
  const previousRules = rules.filter((r) => ["backdated", "closed"].includes(r.state));
  const summary = useMemo(() => ({
    active: activeRules.length,
    previous: previousRules.length,
    total: rows.reduce((sum, row) => sum + Number(row.incentive_amount || 0), 0),
    reviewed: rows.filter((row) => row.received_override).length,
  }), [activeRules.length, previousRules.length, rows]);

  const openNewRule = () => {
    setRuleForm({ ...EMPTY_RULE, date: istDate(), start_date: istDate(), end_date: istDate(), month: istMonth(), tiers: [{ ...EMPTY_TIER }] });
    setRuleOpen(true);
  };

  const openEditRule = (rule) => {
    setRuleForm({
      id: rule.id,
      type: rule.type || "daily",
      name: rule.name || "",
      date: rule.date || istDate(),
      start_date: rule.start_date || istDate(),
      end_date: rule.end_date || istDate(),
      month: rule.month || istMonth(),
      start_time: rule.start_time || "09:00",
      end_time: rule.end_time || "10:00",
      tiers: normalizeTiers(rule.tiers || []),
      all_employees: !!rule.all_employees,
      employee_ids: rule.employee_ids || [],
      auto_delete: rule.auto_delete !== false,
    });
    setRuleOpen(true);
  };

  const saveRule = async () => {
    const tiers = cleanTiers(ruleForm.tiers);
    if (!tiers.length) { toast.error("Add at least one slab"); return; }
    if (!ruleForm.all_employees && !ruleForm.employee_ids.length) { toast.error("Select at least one employee"); return; }
    if (ruleForm.auto_delete && isOlderThanGrace(ruleForm)) {
      toast.error("More than 48 hours have passed. Turn off auto-delete to add this previous incentive.");
      return;
    }
    setSaving(true);
    try {
      if (ruleForm.id) {
        await api.put(`/admin/incentive-rules/${ruleForm.id}`, { ...ruleForm, tiers });
        toast.success("Incentive updated");
      } else {
        await api.post("/admin/incentive-rules", { ...ruleForm, tiers });
        toast.success("Incentive added");
      }
      setRuleOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const closeRule = async (rule) => {
    try {
      await api.post(`/admin/incentive-rules/${rule.id}/close`);
      toast.success("Incentive closed and saved for employees");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteRule = async (rule) => {
    try {
      await api.delete(`/admin/incentive-rules/${rule.id}`);
      toast.success(rule.closed_at ? "Closed incentive deleted. Saved payouts remain." : "Incentive deleted");
      setRuleDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateAllIncentives = async () => {
    setUpdatingAll(true);
    try {
      const res = await api.post(`/admin/incentives/update-all?month=${month}`);
      toast.success(`Updated incentives for ${res.data.employees || 0} employees`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUpdatingAll(false);
    }
  };

  const openEdit = (row) => {
    const bd = row.breakdown || {};
    const ov = row.received_override || {};
    setEditing({
      id: row.employee_id,
      name: row.employee_name,
      daily: ov.daily ?? bd.daily ?? 0,
      hourly: ov.hourly ?? bd.hourly ?? 0,
      weekly: ov.weekly ?? bd.weekly ?? 0,
      monthly: ov.monthly ?? bd.monthly ?? 0,
      manual: ov.manual ?? bd.manual ?? 0,
      note: ov.note || "",
    });
  };

  const saveReceived = async () => {
    setSaving(true);
    try {
      await api.post("/admin/incentives/received", {
        employee_id: editing.id,
        month,
        daily: parseFloat(editing.daily || 0),
        hourly: parseFloat(editing.hourly || 0),
        weekly: parseFloat(editing.weekly || 0),
        monthly: parseFloat(editing.monthly || 0),
        manual: parseFloat(editing.manual || 0),
        note: editing.note,
      });
      toast.success(`Incentive received updated for ${editing.name}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-incentives">
      <div className="card-warm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-overline">Incentives</div>
            <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Rules and payouts</h2>
            <p className="text-sm text-[#5C635E] mt-1">Add daily, hourly, weekly, or monthly incentives and assign them to everyone or selected employees.</p>
          </div>
          <div className="flex items-center justify-end gap-2 flex-wrap w-full lg:w-auto lg:ml-auto">
            <MonthSelect testId="incentive-month" value={month} onChange={setMonth} />
            <button data-testid="update-all-incentives" type="button" onClick={updateAllIncentives} disabled={updatingAll}
              className="border border-[#EAE6DF] hover:border-[#4A5D4E] disabled:opacity-60 rounded-xl px-4 py-2 text-sm inline-flex items-center gap-2">
              <RefreshCw className={`h-3.5 w-3.5 ${updatingAll ? "animate-spin" : ""}`} /> {updatingAll ? "Updating..." : "Update all"}
            </button>
            <button data-testid="add-incentive-rule" type="button" onClick={openNewRule}
              className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 shadow-lg shadow-[#4A5D4E]/20 ring-2 ring-[#DDE7DF]">
              <Plus className="h-4 w-4" /> Add incentive
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <SummaryTile icon={CalendarRange} label="Active" value={summary.active} />
          <SummaryTile icon={Clock} label="Previous" value={summary.previous} tone="red" />
          <SummaryTile icon={Wallet} label={`${month} total`} value={formatINR(summary.total)} />
          <SummaryTile icon={SlidersHorizontal} label="Reviewed" value={`${summary.reviewed}/${rows.length}`} />
        </div>
      </div>

      <RuleSection title="Active incentives" rules={activeRules} empty="No active incentives." onDelete={(r) => setRuleDelete(r)} onEdit={openEditRule} onClose={closeRule} />
      <RuleSection title="Previous incentives" rules={previousRules} empty="No previous incentives." onDelete={(r) => setRuleDelete(r)} onEdit={openEditRule} onClose={closeRule} previous />

      <section className="card-warm overflow-hidden">
        <SectionHeader icon={Users} eyebrow="Employee payouts" title={`Calculated incentives for ${month}`} className="p-5 border-b border-[#EAE6DF]" />
        <EmployeePayoutTable rows={rows} openEdit={openEdit} />
      </section>

      {ruleOpen && (
        <RuleModal
          form={ruleForm}
          setForm={setRuleForm}
          employees={employees}
          save={saveRule}
          saving={saving}
          close={() => setRuleOpen(false)}
        />
      )}
      {ruleDelete && (
        <DeleteRuleDialog
          rule={ruleDelete}
          close={() => setRuleDelete(null)}
          onCloseRule={async () => { await closeRule(ruleDelete); setRuleDelete(null); }}
          onDelete={() => deleteRule(ruleDelete)}
        />
      )}
      {editing && (
        <ReceivedModal editing={editing} setEditing={setEditing} saving={saving} save={saveReceived} />
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tone }) {
  return (
    <div className={`border rounded-lg p-3 bg-[#FDFBF7] ${tone === "red" ? "border-red-200" : "border-[#EAE6DF]"}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8A938D] font-bold"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`mt-2 text-xl font-medium ${tone === "red" ? "text-[#C05746]" : "text-[#2D312E]"}`} style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title, action, className = "" }) {
  return (
    <div className={`flex items-start justify-between gap-3 flex-wrap ${className}`}>
      <div>
        <div className="text-overline flex items-center gap-2"><Icon className="h-3 w-3" /> {eyebrow}</div>
        <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>{title}</h3>
      </div>
      {action}
    </div>
  );
}

function RuleSection({ title, rules, empty, onDelete, onEdit, onClose, previous }) {
  return (
    <section className="card-warm p-5 space-y-4">
      <SectionHeader icon={previous ? Clock : CalendarRange} eyebrow={previous ? "Previous" : "Active"} title={title} />
      {!rules.length ? (
        <div className="text-sm text-[#8A938D] border border-dashed border-[#EAE6DF] rounded-lg p-5 text-center">{empty}</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid={previous ? "previous-incentive-rules" : "active-incentive-rules"}>
          {rules.map((rule) => <RuleCard key={rule.id} rule={rule} onDelete={onDelete} onEdit={onEdit} onClose={onClose} />)}
        </div>
      )}
    </section>
  );
}

function RuleCard({ rule, onDelete, onEdit, onClose }) {
  return (
    <div className={`border rounded-lg p-4 bg-[#FDFBF7] ${["backdated", "closed"].includes(rule.state) ? "border-red-200" : "border-[#EAE6DF]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium text-[#2D312E]">{rule.name}</div>
          <div className="flex gap-1 flex-wrap mt-2">
            <Badge>{rule.type}</Badge>
            <Badge>{formatPeriod(rule)}</Badge>
            <Badge>{rule.all_employees ? "All employees" : `${rule.employee_ids?.length || 0} employees`}</Badge>
            {rule.state === "backdated" && <Badge tone="red">Previous</Badge>}
            {rule.state === "closed" && <Badge tone="red">Closed</Badge>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button data-testid={`edit-incentive-rule-${rule.id}`} type="button" onClick={() => onEdit(rule)}
            className="text-[#4A5D4E] border border-[#DDE7DF] rounded-lg px-2 py-1 inline-flex items-center gap-1 text-xs">
            <Edit3 className="h-3.5 w-3.5" /> Edit
          </button>
          {!rule.closed_at && (
            <button data-testid={`close-incentive-rule-${rule.id}`} type="button" onClick={() => onClose(rule)}
              className="text-[#805B00] border border-amber-200 rounded-lg px-2 py-1 inline-flex items-center gap-1 text-xs">
              <Lock className="h-3.5 w-3.5" /> Close
            </button>
          )}
          <button data-testid={`delete-incentive-rule-${rule.id}`} type="button" onClick={() => onDelete(rule)}
            className="text-[#C05746] border border-red-100 rounded-lg px-2 py-1 inline-flex items-center gap-1 text-xs">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      </div>
      <div className="mt-3 space-y-1">
        {(rule.tiers || []).map((tier, i) => <div key={i} className="text-xs text-[#5C635E]">{formatSlabRule(tier)}</div>)}
      </div>
      {!rule.all_employees && (
        <div className="mt-3 text-xs text-[#8A938D]">{(rule.employee_names || []).join(", ")}</div>
      )}
      {rule.state === "backdated" && (
        <div className="mt-3 text-xs text-[#C05746] bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          This previous period still counts matching donations until it is closed. {rule.auto_delete ? "It will auto-delete after 48 hours." : "It stays until manually deleted."}
        </div>
      )}
      {rule.state === "closed" && (
        <div className="mt-3 text-xs text-[#C05746] bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          This incentive is closed. Payouts are saved, and new donations will not change it.
        </div>
      )}
    </div>
  );
}

function RuleModal({ form, setForm, employees, save, saving, close }) {
  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { ...EMPTY_TIER }] });
  const updateTier = (i, k, v) => setForm({ ...form, tiers: form.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) });
  const removeTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });
  const toggleEmployee = (id) => setForm({
    ...form,
    employee_ids: form.employee_ids.includes(id) ? form.employee_ids.filter((x) => x !== id) : [...form.employee_ids, id],
  });

  return (
    <div data-testid="incentive-rule-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-4xl w-full space-y-4 max-h-[90vh] overflow-y-auto">
        <ModalTitle title={form.id ? "Edit incentive" : "Add incentive"} close={close} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SelectField label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })}
            options={[["daily", "Daily"], ["hourly", "Hourly"], ["weekly", "Weekly"], ["monthly", "Monthly"]]} testid="incentive-rule-type" />
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder={`${form.type} incentive`} />
          <label className="flex items-center gap-2 text-sm text-[#2D312E] md:pt-7">
            <input data-testid="incentive-auto-delete" type="checkbox" checked={form.auto_delete} onChange={(e) => setForm({ ...form, auto_delete: e.target.checked })} />
            Auto-delete after 48 hours
          </label>
        </div>
        <PeriodFields form={form} setForm={setForm} />
        {form.auto_delete && isOlderThanGrace(form) && (
          <div className="text-sm text-[#C05746] bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            More than 48 hours have passed for this incentive period. Turn off auto-delete to save it.
          </div>
        )}
        <div className="border border-[#EAE6DF] rounded-lg p-4 bg-[#FDFBF7] space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-medium text-[#2D312E]">Slabs</div>
              <div className="text-xs text-[#8A938D]">Highest matching slab applies for this incentive.</div>
            </div>
            <button type="button" onClick={addTier} className="text-sm text-[#4A5D4E] hover:underline inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add slab</button>
          </div>
          {form.tiers.map((tier, i) => (
            <TierEditor key={i} tier={tier} i={i} updateTier={updateTier} removeTier={removeTier} />
          ))}
        </div>
        <div className="border border-[#EAE6DF] rounded-lg p-4 bg-[#FDFBF7] space-y-3">
          <div className="font-medium text-[#2D312E]">Assign</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={form.all_employees} onChange={() => setForm({ ...form, all_employees: true, employee_ids: [] })} />
            All employees
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={!form.all_employees} onChange={() => setForm({ ...form, all_employees: false })} />
            Specific employees
          </label>
          {!form.all_employees && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-56 overflow-y-auto border border-[#EAE6DF] rounded-lg p-3 bg-white">
              {employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.employee_ids.includes(emp.id)} onChange={() => toggleEmployee(emp.id)} />
                  {emp.name}
                </label>
              ))}
            </div>
          )}
        </div>
        <ModalActions save={save} saving={saving} saveLabel={form.id ? "Save incentive" : "Add incentive"} cancel={close} />
      </div>
    </div>
  );
}

function PeriodFields({ form, setForm }) {
  if (form.type === "monthly") {
    return <Field label="Month" type="month" value={form.month} onChange={(v) => setForm({ ...form, month: v })} testid="incentive-rule-month" />;
  }
  if (form.type === "weekly") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Start date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="incentive-rule-start" />
        <Field label="End date" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="incentive-rule-end" />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="incentive-rule-date" />
      {form.type === "hourly" && (
        <>
          <Field label="Start time" type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testid="incentive-rule-time-start" />
          <Field label="End time" type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testid="incentive-rule-time-end" />
        </>
      )}
    </div>
  );
}

function EmployeePayoutTable({ rows, openEdit }) {
  return (
    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <table className="w-full text-sm min-w-[950px]">
        <thead>
          <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
            <th className="text-left p-3">Employee</th>
            <th className="text-right p-3">Collection</th>
            <th className="text-right p-3">Breakdown</th>
            <th className="text-left p-3">From incentives</th>
            <th className="text-right p-3">Total</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan="6" className="p-8 text-center text-[#8A938D]">No employees.</td></tr>
          ) : rows.map((r) => {
            const bd = r.breakdown || {};
            const details = bd.rule_breakdown || [];
            return (
              <tr key={r.employee_id} data-testid={`incentive-row-${r.employee_id}`} className="border-t border-[#EAE6DF] align-top">
                <td className="p-3">
                  <div className="font-medium text-[#2D312E]">{r.employee_name}</div>
                  <div className="text-xs text-[#8A938D]">{r.employee_username}</div>
                </td>
                <td className="p-3 text-right text-[#5C635E]">{formatINR(r.monthly_collection)}</td>
                <td className="p-3 text-right text-xs text-[#5C635E]" data-testid={`breakdown-${r.employee_id}`}>
                  D {formatINR(bd.daily || 0)} | H {formatINR(bd.hourly || 0)} | W {formatINR(bd.weekly || 0)} | M {formatINR(bd.monthly || 0)} | Man {formatINR(bd.manual || 0)}
                </td>
                <td className="p-3 text-xs text-[#5C635E]">
                  {details.length ? details.map((d) => (
                    <div key={`${d.rule_id}-${d.type}`} className="mb-1">
                      <span className="font-medium text-[#2D312E]">{d.name}</span> ({d.type}, {d.period}) - {formatINR(d.amount)}
                    </div>
                  )) : <span className="text-[#8A938D]">No matched standalone incentive</span>}
                </td>
                <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(r.incentive_amount)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button data-testid={`edit-incentive-${r.employee_id}`} onClick={() => openEdit(r)} className="text-sm text-[#4A5D4E] hover:underline">Edit received</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TierEditor({ tier, i, updateTier, removeTier }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[170px_1fr_80px_1fr_150px_auto] gap-2 items-end">
      <SelectField label="Condition" value={tier.basis || "amount"} onChange={(v) => updateTier(i, "basis", v)}
        options={[["amount", "Donation amount"], ["target_percent", "% monthly target"]]} />
      {tier.basis === "target_percent" ? (
        <Field label="Target reaches %" type="number" value={tier.min_target_percent ?? 0} onChange={(v) => updateTier(i, "min_target_percent", v)} />
      ) : (
        <Field label="Amount reaches" type="number" value={tier.min_amount ?? 0} onChange={(v) => updateTier(i, "min_amount", v)} />
      )}
      <div className="text-xs text-[#8A938D] pb-2">get</div>
      <Field label="Payout" type="number" value={tier.amount} onChange={(v) => updateTier(i, "amount", v)} />
      <SelectField label="Unit" value={tier.unit || "flat"} onChange={(v) => updateTier(i, "unit", v)}
        options={[["flat", "Flat amount"], ["percent", "% of set amount"]]} />
      <button type="button" onClick={() => removeTier(i)} className="text-[#C05746] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm"><X className="h-4 w-4" /></button>
    </div>
  );
}

function ReceivedModal({ editing, setEditing, saving, save }) {
  return (
    <div data-testid="incentive-edit-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full space-y-4 max-h-[90vh] overflow-y-auto">
        <ModalTitle title={`${editing.name} - incentive received`} close={() => setEditing(null)} />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {["daily", "hourly", "weekly", "monthly", "manual"].map((key) => (
            <Field key={key} label={key} type="number" value={editing[key]} onChange={(v) => setEditing({ ...editing, [key]: v })} />
          ))}
          <div className="md:col-span-5">
            <Field label="Note" value={editing.note || ""} onChange={(v) => setEditing({ ...editing, note: v })} />
          </div>
        </div>
        <div className="text-sm text-[#5C635E] border border-[#EAE6DF] rounded-lg p-3 bg-[#FDFBF7]">
          Total received: <span className="font-semibold text-[#2D312E]">{formatINR(["daily", "hourly", "weekly", "monthly", "manual"].reduce((sum, key) => sum + (parseFloat(editing[key] || 0) || 0), 0))}</span>
        </div>
        <ModalActions save={save} saving={saving} saveLabel="Save received" cancel={() => setEditing(null)} />
      </div>
    </div>
  );
}

function DeleteRuleDialog({ rule, close, onCloseRule, onDelete }) {
  return (
    <div data-testid="delete-incentive-dialog" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-4">
        <ModalTitle title={`Delete ${rule.name || "incentive"}?`} close={close} />
        <div className="text-sm text-[#5C635E] space-y-2">
          {rule.closed_at ? (
            <p>This incentive is already closed. Deleting it will remove the rule card, but saved employee payouts will remain.</p>
          ) : (
            <>
              <p>If you delete without closing, this incentive will be cleared and removed from employee payouts.</p>
              <p>Close first if you want to save the current calculated incentive for employees before deleting.</p>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-3 pt-2 border-t border-[#EAE6DF]">
          {!rule.closed_at && (
            <button type="button" onClick={onCloseRule}
              className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-5 py-2.5 text-sm inline-flex items-center gap-2">
              <Lock className="h-4 w-4" /> Close first
            </button>
          )}
          <button type="button" onClick={onDelete}
            className="bg-[#C05746] hover:bg-[#A9483A] text-white rounded-xl px-5 py-2.5 text-sm inline-flex items-center gap-2">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
          <button type="button" onClick={close} className="text-[#5C635E] text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Badge({ children, tone }) {
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border ${tone === "red" ? "bg-red-50 text-[#C05746] border-red-200" : "bg-white text-[#5C635E] border-[#EAE6DF]"}`}>{children}</span>;
}

function ModalTitle({ title, close }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>{title}</h3>
      <button onClick={close} className="text-[#8A938D]"><X className="h-5 w-5" /></button>
    </div>
  );
}

function ModalActions({ save, saving, saveLabel, cancel }) {
  return (
    <div className="flex gap-3 pt-2 border-t border-[#EAE6DF]">
      <button onClick={save} disabled={saving} className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
        {saving ? "Saving..." : saveLabel}
      </button>
      <button onClick={cancel} className="text-[#5C635E] text-sm">Cancel</button>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, testid }) {
  if (type === "month") return <div><label className="text-xs text-[#5C635E] mb-1 block capitalize">{label}</label><MonthSelect testId={testid} value={value} onChange={onChange} /></div>;
  return (
    <div>
      <label className="text-xs text-[#5C635E] mb-1 block capitalize">{label}</label>
      <input data-testid={testid} type={type} value={value || ""} placeholder={placeholder || ""} onChange={(e) => onChange(e.target.value)}
        inputMode={type === "number" ? "decimal" : undefined}
        className="w-full border border-[#EAE6DF] rounded px-2 py-2 text-sm bg-white" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs text-[#5C635E] mb-1 block">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#EAE6DF] rounded px-2 py-2 text-sm bg-white">
        {options.map(([val, text]) => <option key={val} value={val}>{text}</option>)}
      </select>
    </div>
  );
}

function cleanTiers(list) {
  return (list || [])
    .map((t) => {
      const basis = t.basis === "target_percent" ? "target_percent" : "amount";
      return {
        basis,
        min_amount: basis === "target_percent" ? 0 : parseFloat(t.min_amount || 0),
        min_target_percent: basis === "target_percent" ? parseFloat(t.min_target_percent || 0) : null,
        amount: parseFloat(t.amount || 0),
        unit: t.unit === "percent" ? "percent" : "flat",
      };
    })
    .filter((t) => !Number.isNaN(t.amount) && t.amount > 0 && (t.basis === "target_percent" ? t.min_target_percent > 0 : t.min_amount > 0));
}

function normalizeTiers(list) {
  return (list || []).map((t) => ({
    basis: t.basis === "target_percent" ? "target_percent" : "amount",
    min_amount: t.min_amount ?? 0,
    min_target_percent: t.min_target_percent ?? t.min_amount ?? 0,
    amount: t.amount ?? 0,
    unit: t.unit === "percent" ? "percent" : "flat",
  }));
}

function isOlderThanGrace(form) {
  const key = form.type === "monthly" ? monthEndDate(form.month) : form.type === "weekly" ? form.end_date : form.date;
  if (!key) return false;
  const end = new Date(`${key}T23:59:59+05:30`);
  return Date.now() > end.getTime() + (48 * 60 * 60 * 1000);
}

function monthEndDate(month) {
  if (!month || month.length !== 7) return "";
  const [year, rawMonth] = month.split("-").map(Number);
  const last = new Date(year, rawMonth, 0).getDate();
  return `${month}-${String(last).padStart(2, "0")}`;
}

function formatSlabRule(tier) {
  const payout = tier.unit === "percent" ? `${tier.amount}% of set amount` : formatINR(tier.amount);
  if (tier.basis === "target_percent") return `If reaches ${tier.min_target_percent ?? tier.min_amount ?? 0}% of monthly target, get ${payout}`;
  return `If reaches ${formatINR(tier.min_amount)}, get ${payout}`;
}

function formatPeriod(rule) {
  if (rule.type === "monthly") return rule.month || "";
  if (rule.type === "weekly") return `${rule.start_date || ""} to ${rule.end_date || ""}`;
  if (rule.type === "hourly") return `${rule.date || ""} ${rule.start_time || ""}-${rule.end_time || ""}`;
  return rule.date || "";
}
